import { useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { CATEGORIES } from "../lib/categories";
import { useCommons } from "../lib/store";
import { useTour } from "../tour/TourProvider";
import {
  pendingInvitationsFor,
  projectById,
  personById,
  totalPeopleCommitted,
  totalPeopleNeeded,
  isLibraryVisible,
  youProjectsCount,
  isOverflow,
} from "../lib/domain";

// Home v3 — the six categories ARE the home page.
//
// Above the categories: personal header, sharing banner, fresh-visitor
// tour offer (when applicable), pending invitation card (when a real
// invitation is outstanding).
//
// Below: nothing. No project-feed sections beneath the doors.
// Home is now unambiguously a discovery surface. Category pages
// remain the shelves; Project Detail remains where you pick a
// project up. Personal projects continue to live inside the profile
// entry point at the top-right.
//
// Preview curation is a SEED / DEMO concern: three projects per
// category are tagged `homePreview: true` in seed.json. As of
// Feb 2026 that flag means "preferred for the Home category preview
// slot" — a sort priority, not a whitelist. Uncurated eligible
// Main-Library projects backfill remaining slots (up to 3) if the
// preferred set is depleted (e.g. admin overlay pushed some to
// Overflow). See the previewsByCategory block below for the exact
// two-tier rule.
export default function Home() {
  const nav = useNavigate();
  const { data, user } = useCommons();
  const tour = useTour();

  if (!data || !user) return <div className="c-load">Loading…</div>;

  const invitations = pendingInvitationsFor(user.personId, user.phone, data.invitations);
  // Overflow projects never headline Home; the invitations themselves stay valid.
  const invitation = invitations.find((i) => {
    const p = projectById(data.projects, i.projectId);
    return p && !isOverflow(p, data.projects);
  });
  const invProject = invitation ? projectById(data.projects, invitation.projectId) : undefined;
  const inviter = invitation ? personById(data.people, invitation.inviterId) : undefined;

  // Equals the visible Stewarding + Joined lists on the You page.
  const myProjectsCount = youProjectsCount(user.personId, data.projects, data.pledges);

  // Editorially preferred previews per category.
  //
  // Product rule (Feb 2026): `homePreview: true` is a **preference /
  // sort priority**, not a whitelist. Home shows up to
  // MAX_PREVIEWS_PER_CATEGORY eligible Main-Library projects per
  // category, preferring the curated set first and backfilling from
  // uncurated Main-Library projects when the preferred set is
  // depleted (e.g. because an admin moved a curated project to
  // Overflow).
  //
  // Both tiers share the same eligibility gate — the shared
  // `isLibraryVisible` predicate — which drops drafts, chain-tail-
  // elsewhere historicals, and Overflow projects. That guarantees
  // Home never surfaces a project the visitor cannot find in its
  // category library, and Overflow can never leak into Home.
  //
  // Ordering: within each tier we preserve `data.projects` order
  // (i.e. seed order for canonical projects), so preview selections
  // are deterministic and, absent Overflow changes, identical to the
  // previous curated-only behaviour.
  const MAX_PREVIEWS_PER_CATEGORY = 3;
  const previewsByCategory = CATEGORIES.reduce((acc, c) => {
    const eligible = data.projects.filter(
      (p) =>
        p.category === c.id
        && isLibraryVisible(p, data.projects)
        // Historicals (chain members with a nextAttemptId) render only
        // inside their chain unit on the Category page — never as a
        // standalone preview. On Home, only the chain tail / standalone
        // ever appears, mirroring Category.jsx#buildRenderPlan.
        && !p.nextAttemptId,
    );
    const preferred = eligible.filter((p) => p.homePreview === true);
    const backfill = eligible.filter((p) => p.homePreview !== true);
    acc[c.id] = [...preferred, ...backfill].slice(0, MAX_PREVIEWS_PER_CATEGORY);
    return acc;
  }, {});

  return (
    <div className="c-page" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 32px)" }}>
      <div className="c-header-row">
        <div>
          <div className="c-eyebrow">Commons</div>
          <h1 className="c-h1" style={{ marginTop: 6 }}>Hi {user.name}.<br />What's next?</h1>
          <button
            className="c-tour-chip"
            onClick={() => tour.start(0)}
            data-testid="home-tour-chip"
          >
            <Sparkles size={14} /> Take the tour
          </button>
        </div>
        <button
          type="button"
          className="c-profile-nav"
          onClick={() => nav("/profile")}
          aria-label={`${user.name} — open your Commons (part of ${myProjectsCount} ${myProjectsCount === 1 ? "project" : "projects"})`}
          data-testid="home-profile-btn"
        >
          <span className="c-profile-nav-label">You ✨</span>
          <span
            className="c-profile-nav-count"
            data-testid="home-profile-projects-count"
          >
            {myProjectsCount} {myProjectsCount === 1 ? "project" : "projects"}
          </span>
        </button>
      </div>

      {!tour.freshOffered && !tour.active ? (
        <div className="c-tour-invite" data-testid="tour-fresh-invite">
          <div className="c-tour-invite-eyebrow">🌱 New here?</div>
          <div className="c-tour-invite-title">
            Take a 90-second tour of how Commons works.
          </div>
          <div className="c-tour-invite-row">
            <button
              className="c-btn c-btn-primary"
              onClick={() => tour.start(0)}
              data-testid="tour-fresh-accept"
            >
              Take the tour
            </button>
            <button
              className="c-btn c-btn-ghost"
              onClick={() => tour.dismissFreshOffer()}
              data-testid="tour-fresh-dismiss"
            >
              Explore on my own
            </button>
          </div>
        </div>
      ) : null}

      {invitation && invProject && inviter ? (
        <button
          className="c-invite-card"
          onClick={() => nav(`/project/${invProject.id}?invitedBy=${inviter.id}`)}
          data-testid="home-invitation-card"
        >
          <div className="c-invite-eyebrow">✉ Invitation</div>
          <div className="c-invite-title">
            {invProject.emoji}  {inviter.name} invited you to make {invProject.title} happen.
          </div>
          <div className="c-invite-sub">
            {totalPeopleCommitted(invProject, data.pledges)} of {totalPeopleNeeded(invProject)} committed. Want to be one of them?
          </div>
          <div className="c-invite-cta">
            See the invitation <ArrowRight size={16} />
          </div>
        </button>
      ) : null}

      {/* Editorial thesis + soft doorway. Sits between the personal
          Home introduction and the six category doors, so the grid
          below reads as a set of possible answers. Not a CTA, not a
          banner — a question written in the margin. The action beneath
          is intentionally quiet: imagine first, decide to act second. */}
      <section className="c-thesis" data-testid="home-thesis" data-tour="home-thesis">
        <p className="c-thesis-question">
          What would you do if enough people would help?
        </p>
        <button
          type="button"
          className="c-thesis-doorway"
          onClick={() => nav("/create")}
          data-testid="home-thesis-doorway"
        >
          Start a project <span aria-hidden="true">→</span>
        </button>
      </section>

      {/* Discovery: the six doors ARE the Home page below the personal
          header. Each door contains three editorially-chosen previews
          (emoji + title only) so the visitor sees the range of what
          lives behind each lens without any of the project mechanics
          leaking to Home. Whole card is one tap target that opens the
          category. */}
      <div className="c-section c-discovery" data-testid="section-discovery" data-tour="category-grid">
        <div className="c-discovery-grid">
          {CATEGORIES.map((c, i) => {
            const previews = previewsByCategory[c.id] || [];
            // Accessible name mirrors the exact number of previews the
            // tile actually renders — never lies about "Three examples"
            // when the tile shows fewer.
            const numberWord = ["", "One example", "Two examples", "Three examples"][previews.length] || `${previews.length} examples`;
            const examplesSentence = previews.length
              ? ` ${numberWord} inside: ${previews.map((p) => p.title).join(", ")}.`
              : "";
            return (
              <button
                key={c.id}
                type="button"
                className="c-cat-tile c-cat-tile-lg"
                onClick={() => nav(`/commons/${c.id}`)}
                // Deterministic per-index asymmetric radii so the six
                // read as personalities. Same rotation across renders.
                style={{
                  background: c.surface,
                  color: c.ink,
                  borderRadius:
                    i % 3 === 0
                      ? "28px 18px 26px 20px"
                      : i % 3 === 1
                        ? "18px 28px 20px 26px"
                        : "22px 22px 28px 18px",
                  "--cat-accent": c.accent,
                }}
                data-testid={`cat-tile-${c.id}`}
                aria-label={`Open ${c.name}: ${c.descriptor.replace(/…$/, "")}.${examplesSentence}`}
              >
                <div className="c-cat-tile-head">
                  <div className="c-cat-tile-emoji" aria-hidden="true">{c.emoji}</div>
                  <div className="c-cat-tile-titleblock">
                    <div className="c-cat-tile-name">{c.name}</div>
                  </div>
                </div>
                <div className="c-cat-tile-desc">{c.descriptor}</div>
                <ul className="c-cat-tile-previews" aria-hidden="true">
                  {previews.map((p) => (
                    <li
                      key={p.id}
                      className="c-cat-tile-preview"
                      data-testid={`cat-preview-${c.id}-${p.id}`}
                    >
                      <span className="c-cat-tile-preview-emoji">{p.emoji}</span>
                      <span className="c-cat-tile-preview-title">{p.title}</span>
                    </li>
                  ))}
                </ul>
                <div className="c-cat-tile-explore" aria-hidden="true">
                  Explore <ArrowRight size={14} />
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Unobtrusive footer link to the About page. Deliberately quiet:
          plain text, muted color, centered — reads like a byline rather
          than a navigation element. */}
      <div className="c-home-footer">
        <button
          type="button"
          className="c-about-link"
          onClick={() => nav("/about")}
          data-testid="home-about-link"
        >
          About Commons
        </button>
      </div>
    </div>
  );
}
